// packages/web-ui/src/panels/views/layout-builder.ts
//
// W4-S1 — TypeScript port of `tools/view-renderer/reactflow-app/src/layout.js`.
// Pure ESM, browser-safe, no `node:` imports. Honours story AC #5 + Arch §9.10.

import ELK from 'elkjs/lib/elk.bundled.js';
import type { Edge, Node } from '@xyflow/react';
import { applyViewScope, defaultLayoutIntent } from '@tessa-lean/base';
import type {
  ReactFlowLayoutJson,
  LayoutIntent,
  LayoutDirection,
  LayoutIntentAlgorithm,
  LayoutSpacing,
} from '@tessa-lean/base/api-schemas/index.js';

// ---- ELK option blocks (verbatim from layout.js) ----------------------------

export const ROOT_OPTS = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
  // Wider layer gaps + node spacing open up clean orthogonal routing channels
  // between the subsystem boxes (the airy, gridded look of the reference
  // architecture diagram). `aspectRatio` nudges the layered placement toward a
  // balanced grid rather than one long horizontal row; favouring straight
  // edges keeps inter-subsystem flows visually direct. These are placement /
  // spacing knobs only — the cross-container routing coordinate-frame contract
  // is a property of INCLUDE_CHILDREN + the SCA edge-distribution pass and is
  // unaffected.
  'elk.layered.spacing.nodeNodeBetweenLayers': '140',
  'elk.spacing.nodeNode': '90',
  'elk.spacing.edgeNode': '28',
  'elk.spacing.edgeEdge': '18',
  'elk.aspectRatio': '1.6',
  'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
  'elk.layered.nodePlacement.favorStraightEdges': 'true',
  'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
  'elk.edgeRouting': 'ORTHOGONAL',
  'elk.layered.crossingMinimization.semiInteractive': 'true',
  'elk.portConstraints': 'FIXED_SIDE',
} as const;

export const CONTAINER_OPTS = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  'elk.layered.spacing.nodeNodeBetweenLayers': '90',
  'elk.spacing.nodeNode': '60',
  'elk.spacing.edgeNode': '24',
  'elk.padding': '[top=64,left=36,bottom=28,right=36]',
  'elk.portConstraints': 'FIXED_SIDE',
} as const;

/**
 * Packing layout for containers whose children carry NO internal edges (the
 * common IBD case: a subsystem that merely *holds* component blocks, with all
 * connections drawn between subsystem-level ports). `layered` would stack such
 * edge-less children single-file along one axis — a 9-child subsystem becomes a
 * ~9-row tall column that dwarfs the rest of the diagram. `box` packs them into
 * a compact rectangle (a tidy multi-column grid) honouring a target aspect
 * ratio, so the container stays small and the overall layout reads as a grid —
 * the polished-architecture look. Containers that DO have internal edges keep
 * `CONTAINER_OPTS` (layered) so their port-to-port routing is preserved.
 *
 * `elk.box` lays children out and reports their positions in the container's
 * local frame exactly like `layered` does, so the `collectEdgeRoutes()`
 * coordinate-frame contract is unaffected. The top padding leaves room for the
 * container header drawn by `<PartNode>`'s `ContainerBody`.
 */
export const PACKING_OPTS = {
  'elk.algorithm': 'org.eclipse.elk.box',
  'elk.box.packingMode': 'SIMPLE',
  'elk.aspectRatio': '2.4',
  'elk.expandNodes': 'false',
  'elk.spacing.nodeNode': '18',
  'elk.padding': '[top=60,left=20,bottom=20,right=20]',
  'elk.portConstraints': 'FIXED_SIDE',
} as const;

export const LEAF_W = 260;
export const HEADER_H = 50;
export const ATTR_H = 14;
export const PORT_GAP = 18;
export const PORT_TOP_PAD = 14;

/** Arch §9.10 — virtualisation threshold for `onlyRenderVisibleElements`. */
export const VIRTUALISE_THRESHOLD = 50;

// ---- LayoutIntent → ELK option mapping (LV1-S3) -----------------------------
//
// Tier 1 of the layout-control epic: drive `elk.direction` / `elk.algorithm` /
// the spacing knobs from a validated `LayoutIntent` (the LV1-S1 DSL) instead of
// the hard-coded constants. The mapping is total + pure; the *default* intent
// (`RIGHT` / `layered` / `normal`) reproduces today's `ROOT_OPTS` /
// `CONTAINER_OPTS` byte-for-byte, so the no-intent / switch-OFF path is
// unchanged.

/**
 * ELK direction token for an intent direction. ELK and the DSL share the exact
 * four tokens (`RIGHT|LEFT|DOWN|UP`), so this is an identity passthrough — kept
 * as a named helper purely so the mapping has a single drift-proof site.
 */
export function mapDirection(direction: LayoutDirection): LayoutDirection {
  return direction;
}

/** Horizontal directions lay layers left↔right; vertical ones top↔bottom. */
function isVerticalDirection(direction: LayoutDirection): boolean {
  return direction === 'DOWN' || direction === 'UP';
}

/**
 * The ELK port sides for in/out ports given the layout direction. Horizontal
 * directions keep the today-default `WEST` (in) / `EAST` (out); vertical
 * directions flip to `NORTH` (in) / `SOUTH` (out) so the port axis follows the
 * flow axis instead of fighting it (AC #2).
 */
export function portSidesFor(direction: LayoutDirection): {
  inSide: 'WEST' | 'EAST' | 'NORTH' | 'SOUTH';
  outSide: 'WEST' | 'EAST' | 'NORTH' | 'SOUTH';
} {
  if (isVerticalDirection(direction)) {
    return { inSide: 'NORTH', outSide: 'SOUTH' };
  }
  return { inSide: 'WEST', outSide: 'EAST' };
}

/**
 * Map a DSL algorithm onto the concrete ELK algorithm id. `layered` is the
 * today-default and the only algorithm that preserves ELK port-routing /
 * `INCLUDE_CHILDREN` for nested IBDs (see the flat-graph gate in
 * `runElkLayout`). `tree → mrtree`, `force → force`, `grid → rectpacking`.
 *
 * `radial → force` (NOT `org.eclipse.elk.radial`): elkjs's radial
 * implementation requires a tree — on the cyclic graphs KB views routinely
 * produce it either returns EVERY node at the same coordinate (a total
 * pile-up; reproduced with the debug-workspace battery-context view, all
 * nodes at (142,69)) or, for port-less nodes, recurses until "Maximum call
 * stack size exceeded". `force` yields the organic hub-and-spoke look a
 * "radial/circular" wish is after and is safe on any graph shape.
 */
export function mapAlgorithm(algorithm: LayoutIntentAlgorithm): string {
  switch (algorithm) {
    case 'layered':
      return 'layered';
    case 'tree':
      return 'mrtree';
    case 'force':
      return 'force';
    case 'grid':
      return 'rectpacking';
    case 'radial':
      return 'force';
    default: {
      // Exhaustiveness guard — a new enum member would fail typecheck here.
      const _never: never = algorithm;
      return _never;
    }
  }
}

/**
 * Spacing multipliers for the three coarse presets. `normal` is exactly `1`, so
 * the resolved knobs equal today's literal strings (byte-identical default);
 * `compact` tightens, `spacious` opens up. The base values differ per scope
 * (root vs container) so both stay byte-identical at `normal`.
 */
const SPACING_MULTIPLIER: Record<LayoutSpacing, number> = {
  compact: 0.6,
  normal: 1,
  spacious: 1.5,
};

/**
 * Resolve the two spacing knobs (`elk.spacing.nodeNode`,
 * `elk.layered.spacing.nodeNodeBetweenLayers`) for a scope, scaling the
 * today-base values by the preset multiplier. At `normal` the multiplier is
 * `1`, and the base values are the exact today numbers, so the returned strings
 * are byte-identical to the hard-coded constants. Non-`normal` presets round to
 * the nearest integer (ELK option values are stringified integers).
 */
function mapSpacing(
  spacing: LayoutSpacing,
  base: { nodeNode: number; nodeNodeBetweenLayers: number },
): { nodeNode: string; nodeNodeBetweenLayers: string } {
  const m = SPACING_MULTIPLIER[spacing];
  if (m === 1) {
    // Preserve the literal today-strings — never re-stringify via `* 1`, which
    // would still be byte-identical here but is left explicit to make the
    // switch-OFF parity guarantee impossible to break by a future refactor.
    return {
      nodeNode: String(base.nodeNode),
      nodeNodeBetweenLayers: String(base.nodeNodeBetweenLayers),
    };
  }
  return {
    nodeNode: String(Math.round(base.nodeNode * m)),
    nodeNodeBetweenLayers: String(Math.round(base.nodeNodeBetweenLayers * m)),
  };
}

/** Today's root spacing base (matches `ROOT_OPTS`). */
const ROOT_SPACING_BASE = { nodeNode: 90, nodeNodeBetweenLayers: 140 } as const;
/** Today's container spacing base (matches `CONTAINER_OPTS`). */
const CONTAINER_SPACING_BASE = { nodeNode: 60, nodeNodeBetweenLayers: 90 } as const;

/**
 * Build the root ELK layout options from an intent. Starts from the frozen
 * today-default `ROOT_OPTS` and overlays direction / algorithm / spacing. When
 * `intent` reproduces the default (`RIGHT` / `layered` / `normal`) the result
 * deep-equals `ROOT_OPTS` (switch-OFF parity). `effectiveAlgorithm` lets the
 * caller pass the post-gate algorithm (a downgrade to `layered`).
 */
export function buildRootOpts(
  intent: LayoutIntent,
  effectiveAlgorithm: LayoutIntentAlgorithm = intent.algorithm,
): Record<string, string> {
  const spacing = mapSpacing(intent.spacing, ROOT_SPACING_BASE);
  return {
    ...ROOT_OPTS,
    'elk.algorithm': mapAlgorithm(effectiveAlgorithm),
    'elk.direction': mapDirection(intent.direction),
    'elk.layered.spacing.nodeNodeBetweenLayers': spacing.nodeNodeBetweenLayers,
    'elk.spacing.nodeNode': spacing.nodeNode,
  };
}

/**
 * Build the per-container ELK layout options from an intent. Containers ALWAYS
 * use `layered` — the requested top-level algorithm is a root-only knob;
 * pushing mrtree/force/radial/rectpacking into a container would break the
 * cross-boundary port-routing the `collectEdgeRoutes` contract depends on. Only
 * `direction` + `spacing` flow through. Default intent deep-equals
 * `CONTAINER_OPTS`.
 */
export function buildContainerOpts(intent: LayoutIntent): Record<string, string> {
  const spacing = mapSpacing(intent.spacing, CONTAINER_SPACING_BASE);
  return {
    ...CONTAINER_OPTS,
    'elk.direction': mapDirection(intent.direction),
    'elk.layered.spacing.nodeNodeBetweenLayers': spacing.nodeNodeBetweenLayers,
    'elk.spacing.nodeNode': spacing.nodeNode,
  };
}

/** Algorithms that drop ELK port-routing / `INCLUDE_CHILDREN` and so are only
 * safe on a FLAT graph (no nested containers). On a nested graph they would
 * abort `collectEdgeRoutes`' port-routed-polyline contract, so they downgrade
 * to `layered`. Only `layered` survives nesting. */
const NESTING_UNSAFE_ALGORITHMS: ReadonlySet<LayoutIntentAlgorithm> = new Set<
  LayoutIntentAlgorithm
>(['tree', 'force', 'grid', 'radial']);

/** A downgrade record surfaced on the layout result when a nesting-unsafe
 * algorithm was requested against a nested graph and forced back to `layered`. */
export interface LayoutDowngrade {
  requested: LayoutIntentAlgorithm;
  applied: 'layered';
  reason: string;
}

// ---- Public types -----------------------------------------------------------

export interface PortRef {
  name: string;
  direction?: 'in' | 'out' | 'inout';
  /**
   * Optional changeset-preview tag. When set, the port row in `<PartNode>`
   * is color-coded (green/amber/red) to mark it as added / updated /
   * removed in a pending changeset. Absent for ordinary view rendering.
   */
  changeKind?: 'added' | 'updated' | 'removed' | 'unchanged';
  /**
   * Optional 1-based changeset ordinal. When set, `<PartNode>` renders a
   * small numbered badge next to the port label that mirrors the same
   * number on the `[N]`-prefixed row in the `<KbChangeTree>` shell.
   */
  changeIndex?: number;
}

export interface PartNodeData {
  name?: string;
  /**
   * SysML v2 stereotype keyword (e.g. `part def`, `part`, `port def`). Drawn
   * inside guillemets as `«part def»` above the block name. Falls back to
   * the legacy `category` field for layouts authored before the rename, and
   * finally to a SysML v2 sensible default (`part def`) — *never* `block`,
   * which is a SysML v1 stereotype.
   */
  kind?: string;
  /** @deprecated Pre-rename alias for `kind`. Kept for old layout.json files. */
  category?: string;
  package?: string;
  attributes?: ReadonlyArray<{ name: string; type?: string; value?: string }>;
  portsIn?: ReadonlyArray<PortRef>;
  portsOut?: ReadonlyArray<PortRef>;
  isContainer?: boolean;
  /** Generic display label used by non-part node kinds. */
  label?: string;
  /**
   * Optional changeset-preview tag. When set, the block's border and fill
   * pick a color variant (green = added, amber = updated, red dashed =
   * removed). Absent for ordinary view rendering — defaults to the
   * neutral theme.
   */
  changeKind?: 'added' | 'updated' | 'removed' | 'unchanged';
  /**
   * Optional 1-based changeset ordinal. When set, `<PartNode>` renders
   * a numbered badge in the top-right corner that mirrors the same `[N]`
   * number on the matching row of the `<KbChangeTree>` shell — so the
   * user can scan the change list and the diagram side by side and tell
   * exactly which entity each badge represents.
   */
  changeIndex?: number;
  /**
   * DLA emphasis overlay — `emphasize` gives the block an accent ring (paired
   * with the pre-ELK enlarge), `deemphasize` dims it. Projected from
   * `layoutIntent.emphasis`/`deemphasis` in `buildReactFlowGraph`; orthogonal to
   * the changeset `changeKind` diff overlay. Absent ⇒ neutral.
   */
  emphasisKind?: 'emphasize' | 'deemphasize';
  [key: string]: unknown;
}

export interface PortNodeData {
  name?: string;
  label?: string;
  [key: string]: unknown;
}

export interface InterfaceNodeData {
  name?: string;
  label?: string;
  [key: string]: unknown;
}

export type ConnectionKind =
  | 'energy'
  | 'material'
  | 'mechanical'
  | 'information'
  | 'reference';

export interface ConnectionEdgeData {
  label?: string;
  kind?: ConnectionKind;
  /**
   * Number of KB connections this single rendered connector stands in for.
   * Set by `coalesceOverlappingEdges` when two or more connections share the
   * exact same port pair (they draw as one indistinguishable line). `> 1`
   * surfaces a compact `×N` marker on the connector label so the fold is not
   * silent; absent / `1` for an ordinary single connector.
   */
  multiplicity?: number;
  /**
   * Optional changeset-preview tag. When set, the edge stroke colour
   * overrides the per-flow-kind stroke (green/amber/red for
   * added/updated/removed; removed edges are dashed). Absent for ordinary
   * view rendering — falls back to the per-kind style.
   */
  changeKind?: 'added' | 'updated' | 'removed' | 'unchanged';
  /**
   * Optional 1-based changeset ordinal. When set, `<ConnectionEdge>`
   * renders a numbered badge in the edge label region that mirrors the
   * same `[N]` number on the matching row of the `<KbChangeTree>` shell.
   */
  changeIndex?: number;
  /**
   * DLA emphasis overlay — `deemphasize` fades an edge touching a dimmed node.
   * Projected in `buildReactFlowGraph`; orthogonal to the `changeKind` diff.
   */
  emphasisKind?: 'emphasize' | 'deemphasize';
  [key: string]: unknown;
}

// ---- runElkLayout -----------------------------------------------------------

interface ReactFlowNodeWithParent extends Node {
  parentId?: string;
  width?: number;
  height?: number;
}

interface ElkPortDef {
  id: string;
  properties: Record<string, string>;
}

interface ElkEdgeInput {
  id: string;
  sources: string[];
  targets: string[];
}

interface ElkChild {
  id: string;
  ports: ElkPortDef[];
  layoutOptions?: Record<string, string>;
  children?: ElkChild[];
  edges?: ElkEdgeInput[];
  width?: number;
  height?: number;
}

interface ElkPoint {
  x: number;
  y: number;
}

interface ElkResultEdgeSection {
  startPoint: ElkPoint;
  endPoint: ElkPoint;
  bendPoints?: ElkPoint[];
}

interface ElkResultEdge {
  id: string;
  sections?: ElkResultEdgeSection[];
}

interface ElkResultNode {
  id: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  children?: ElkResultNode[];
  edges?: ElkResultEdge[];
}

const elk = new ELK();

function leafHeight(node: Node): number {
  const data = (node.data ?? {}) as PartNodeData;
  const portsIn = data.portsIn ?? [];
  const portsOut = data.portsOut ?? [];
  const portCount = Math.max(portsIn.length, portsOut.length);
  const attrCount = (data.attributes ?? []).length;
  return (
    HEADER_H +
    (attrCount > 0 ? attrCount * ATTR_H + 8 : 0) +
    portCount * PORT_GAP +
    PORT_TOP_PAD * 2
  );
}

/**
 * Run elkjs layered hierarchical layout. Pure async function; no DOM access.
 * Returns nodes with positions + dimensions, plus the overall canvas extent.
 */
export async function runElkLayout(
  nodes: Node[],
  edges: Edge[],
  intent?: LayoutIntent,
): Promise<{
  nodes: Node[];
  edges: Edge[];
  width: number;
  height: number;
  layoutDowngrade?: LayoutDowngrade;
}> {
  // Resolve to the byte-identical-to-today default when no intent is supplied
  // (switch-OFF parity for all existing call sites). Reuses the LV1-S1 SSOT
  // factory so the no-intent default can never drift from the DSL baseline.
  const activeIntent: LayoutIntent = intent ?? defaultLayoutIntent();
  const { inSide, outSide } = portSidesFor(activeIntent.direction);

  const portIdByKey = new Map<string, string>();
  const childrenByParent = new Map<string, Node[]>();
  const rootNodes: Node[] = [];
  const parentByNode = new Map<string, string | undefined>();
  const elkChildById = new Map<string, ElkChild>();

  for (const n of nodes as ReactFlowNodeWithParent[]) {
    parentByNode.set(n.id, n.parentId);
    if (n.parentId !== undefined) {
      const arr = childrenByParent.get(n.parentId) ?? [];
      arr.push(n);
      childrenByParent.set(n.parentId, arr);
    } else {
      rootNodes.push(n);
    }
  }

  // ── Which containers actually hold internal edges? ─────────────────────────
  // A container that holds edges between its own children must keep the
  // `layered` algorithm so ELK routes those edges port-to-port. A container
  // that merely *holds* edge-less child blocks (the typical subsystem case)
  // gets the `box` packing algorithm instead so its children pack into a
  // compact grid rather than one tall single-file column. We reuse the
  // smallest-common-ancestor (SCA) logic — the deepest node that is an
  // ancestor of BOTH endpoints; for a sibling-pair intra-container edge that
  // is their shared parent container.
  const ancestorChainOf = (nodeId: string): string[] => {
    const chain: string[] = [];
    let cur: string | undefined = nodeId;
    while (cur !== undefined) {
      chain.push(cur);
      cur = parentByNode.get(cur);
    }
    return chain;
  };
  const scaOf = (a: string, b: string): string | null => {
    const chainA = ancestorChainOf(a);
    const setB = new Set(ancestorChainOf(b));
    for (let i = 1; i < chainA.length; i += 1) {
      if (setB.has(chainA[i]!)) return chainA[i]!;
    }
    return null;
  };
  const containersWithInternalEdges = new Set<string>();
  // A container is also kept on `layered` if it merely HOLDS a child that is an
  // endpoint of a cross-boundary edge (an edge whose other end is outside the
  // container). The `box` packing algorithm cannot route an edge that crosses
  // its boundary and throws `UnsupportedGraphException`, aborting the whole ELK
  // layout (the renderer would then fall back to origin-stacked nodes). For
  // each edge endpoint we walk its ancestor chain UP TO — but not including —
  // the edge's SCA; every container strictly between the endpoint and the SCA
  // has that cross-boundary edge passing through one of its descendants and so
  // must stay on `layered` to route it.
  const boundaryEndpointAncestors = new Set<string>();
  for (const e of edges) {
    const sca = scaOf(e.source, e.target); // null ⇒ no common container (root)
    if (sca !== null) containersWithInternalEdges.add(sca);
    for (const ep of [e.source, e.target]) {
      let cur = parentByNode.get(ep);
      while (cur !== undefined && cur !== sca) {
        boundaryEndpointAncestors.add(cur);
        cur = parentByNode.get(cur);
      }
    }
  }
  /** A container may use `box` packing only if no edge needs routing inside it. */
  const isPackable = (id: string): boolean =>
    !containersWithInternalEdges.has(id) && !boundaryEndpointAncestors.has(id);

  // ── Flat-graph gate for nesting-unsafe algorithms (AC #3) ──────────────────
  // mrtree/force/radial/rectpacking drop ELK port-routing / `INCLUDE_CHILDREN`
  // and would abort the `collectEdgeRoutes` port-routed-polyline contract on a
  // nested graph. The graph is "flat" iff no node declares a parent (no
  // container has children). When the requested algorithm is nesting-unsafe AND
  // the graph is nested, fall back to `layered` for the ROOT algorithm and
  // record the downgrade. Containers always stay `layered`/`box` regardless.
  const isFlatGraph = childrenByParent.size === 0;
  let layoutDowngrade: LayoutDowngrade | undefined;
  let effectiveAlgorithm: LayoutIntentAlgorithm = activeIntent.algorithm;
  if (
    NESTING_UNSAFE_ALGORITHMS.has(activeIntent.algorithm) &&
    !isFlatGraph
  ) {
    layoutDowngrade = {
      requested: activeIntent.algorithm,
      applied: 'layered',
      reason:
        `algorithm '${activeIntent.algorithm}' drops ELK port-routing / ` +
        `INCLUDE_CHILDREN and is unsafe on a nested graph; fell back to 'layered'`,
    };
    effectiveAlgorithm = 'layered';
  }
  const containerOpts = buildContainerOpts(activeIntent);

  function buildElkChild(n: Node): ElkChild {
    const data = (n.data ?? {}) as PartNodeData;
    const portsIn = data.portsIn ?? [];
    const portsOut = data.portsOut ?? [];
    const ports: ElkPortDef[] = [];
    for (const p of portsIn) {
      const id = `${n.id}::${p.name}::in`;
      ports.push({ id, properties: { 'port.side': inSide } });
      portIdByKey.set(`${n.id}::${p.name}`, id);
    }
    for (const p of portsOut) {
      const id = `${n.id}::${p.name}::out`;
      ports.push({ id, properties: { 'port.side': outSide } });
      portIdByKey.set(`${n.id}::${p.name}`, id);
    }
    const elkChild: ElkChild = { id: n.id, ports, edges: [] };
    elkChildById.set(n.id, elkChild);
    const kids = childrenByParent.get(n.id) ?? [];
    if (kids.length > 0) {
      elkChild.layoutOptions = isPackable(n.id)
        ? { ...PACKING_OPTS }
        : containerOpts;
      elkChild.children = kids.map(buildElkChild);
    } else {
      elkChild.width = LEAF_W;
      elkChild.height = leafHeight(n);
    }
    return elkChild;
  }

  const elkChildren = rootNodes.map(buildElkChild);

  // ── Distribute edges into their smallest-common-ancestor container ─────
  //
  // Critical for routing: when an edge is placed inside a container's
  // `edges` array, ELK keeps it there and reports section coordinates in
  // that container's local frame. If we drop everything at the root level,
  // ELK still routes correctly internally but the coordinate frame of the
  // resulting `section.bendPoints` becomes hard to determine from outside
  // — which is exactly what broke the polyline path earlier.
  //
  // The SCA is the deepest node that is an ancestor of BOTH endpoints (see
  // `scaOf` above). For sibling-pair IBD edges (the typical case) that's their
  // common parent. When the SCA is `null`, the edge goes into the root's
  // `edges` array.
  const rootEdges: ElkEdgeInput[] = [];
  for (const e of edges) {
    const srcRaw = (e.sourceHandle ?? '').replace(/\.[st]$/, '');
    const dstRaw = (e.targetHandle ?? '').replace(/\.[st]$/, '');
    const src = srcRaw ? portIdByKey.get(`${e.source}::${srcRaw}`) ?? e.source : e.source;
    const dst = dstRaw ? portIdByKey.get(`${e.target}::${dstRaw}`) ?? e.target : e.target;
    const elkEdge: ElkEdgeInput = { id: e.id, sources: [src], targets: [dst] };
    const sca = scaOf(e.source, e.target);
    if (sca === null) {
      rootEdges.push(elkEdge);
    } else {
      const container = elkChildById.get(sca);
      if (container === undefined) {
        rootEdges.push(elkEdge);
      } else {
        container.edges!.push(elkEdge);
      }
    }
  }

  // The elkjs typing surface has a permissive index signature for layout
  // options; we trust the verbatim-port option blocks to be valid.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- elkjs typings narrow children too aggressively
  const layout: any = await elk.layout({
    id: 'root',
    layoutOptions: buildRootOpts(activeIntent, effectiveAlgorithm),
    children: elkChildren as unknown as never,
    edges: rootEdges as unknown as never,
  });

  const elkById = new Map<string, ElkResultNode>();
  function walk(elkNode: ElkResultNode): void {
    elkById.set(elkNode.id, elkNode);
    for (const c of elkNode.children ?? []) walk(c);
  }
  for (const c of (layout.children ?? []) as ElkResultNode[]) walk(c);

  const positioned: Node[] = nodes.map((n) => {
    const e = elkById.get(n.id);
    if (!e) return n;
    return {
      ...n,
      position: { x: e.x ?? 0, y: e.y ?? 0 },
      width: e.width,
      height: e.height,
      style: { ...(n.style ?? {}), width: e.width, height: e.height },
    };
  });

  const sorted = topoSortParentsFirst(positioned as Array<Node & { parentId?: string }>);

  // ── ELK edge routing extraction ──────────────────────────────────────────
  // ELK with `hierarchyHandling: INCLUDE_CHILDREN` computes per-edge bend
  // points that explicitly route around node bodies. React Flow's built-in
  // smoothstep does NOT avoid nodes, so without these bend points edges
  // visibly cut through block backgrounds.
  //
  // ELK section coordinates are relative to the edge's containing block —
  // typically the smallest common ancestor of its source + target. We walk
  // the result tree, accumulating per-container absolute offsets, then
  // translate every section point into the (global) coordinate space React
  // Flow draws in.
  const edgeRoutes = new Map<string, ElkPoint[]>();
  function collectEdgeRoutes(
    container: ElkResultNode | { children?: ElkResultNode[]; edges?: ElkResultEdge[] },
    absX: number,
    absY: number,
  ): void {
    const containerEdges = (container as { edges?: ElkResultEdge[] }).edges ?? [];
    for (const elkEdge of containerEdges) {
      const section = elkEdge.sections?.[0];
      if (section === undefined) continue;
      const start = { x: section.startPoint.x + absX, y: section.startPoint.y + absY };
      const end = { x: section.endPoint.x + absX, y: section.endPoint.y + absY };
      const bends = (section.bendPoints ?? []).map((p) => ({
        x: p.x + absX,
        y: p.y + absY,
      }));
      edgeRoutes.set(elkEdge.id, [start, ...bends, end]);
    }
    for (const child of container.children ?? []) {
      collectEdgeRoutes(child, absX + (child.x ?? 0), absY + (child.y ?? 0));
    }
  }
  collectEdgeRoutes(layout as { children?: ElkResultNode[]; edges?: ElkResultEdge[] }, 0, 0);

  // ── Edge-routing trust gate ────────────────────────────────────────────
  // Only the `layered` (Sugiyama) algorithm produces the port-anchored,
  // node-avoiding ORTHOGONAL edge sections the `collectEdgeRoutes` polyline
  // contract was built for. `mrtree` / `force` / `rectpacking` (the `tree` /
  // `force` / `radial` / `grid` DSL algorithms) place nodes as a tree / by
  // physics / by packing and route their edges crudely — on a cyclic KB graph
  // (e.g. the coolant loop) with our FIXED opposite port-sides this yields
  // gross wrap-around detours: a back/cross edge exits the OUT-port side and
  // has to loop the entire diagram to reach the IN-port side (observed: a
  // CoolantPump→BatteryPack coolant edge routed the full canvas width+height).
  // For those algorithms we do NOT attach ELK's bend points, so `ConnectionEdge`
  // falls back to React Flow's port-aware `getSmoothStepPath` — a local,
  // bounded orthogonal route that never wraps around the whole layout.
  //
  // The gate keys on `effectiveAlgorithm`, so this is inert for the default
  // (`layered`) path — the no-intent / switch-OFF render stays byte-identical.
  // A nesting-unsafe algorithm requested against a NESTED graph is already
  // downgraded to `layered` above (so `effectiveAlgorithm === 'layered'`) and
  // keeps ELK routing; only a genuinely FLAT non-layered graph takes the
  // smoothstep path, which is exactly where ELK's routing was unreliable.
  const trustElkRouting = effectiveAlgorithm === 'layered';
  const routedEdges: Edge[] = edges.map((e) => {
    if (!trustElkRouting) return e;
    const route = edgeRoutes.get(e.id);
    if (route === undefined || route.length < 2) return e;
    const baseData = (e.data ?? {}) as Record<string, unknown>;
    return { ...e, data: { ...baseData, elkBendPoints: route } };
  });

  return {
    nodes: sorted,
    edges: routedEdges,
    width: typeof layout.width === 'number' ? layout.width : 0,
    height: typeof layout.height === 'number' ? layout.height : 0,
    // `layoutDowngrade` is additive — omitted when no downgrade occurred, so
    // existing `{ nodes, edges, width, height }` destructuring is unaffected.
    ...(layoutDowngrade !== undefined ? { layoutDowngrade } : {}),
  };
}

// ---- topoSortParentsFirst ---------------------------------------------------

export function topoSortParentsFirst<T extends { id: string; parentId?: string }>(
  nodes: T[],
): T[] {
  const byId = new Map<string, T>(nodes.map((n) => [n.id, n]));
  const out: T[] = [];
  const seen = new Set<string>();
  function visit(n: T): void {
    if (seen.has(n.id)) return;
    seen.add(n.id);
    if (n.parentId !== undefined && byId.has(n.parentId)) {
      visit(byId.get(n.parentId) as T);
    }
    out.push(n);
  }
  for (const n of nodes) visit(n);
  return out;
}

// ---- buildReactFlowGraph ----------------------------------------------------

const NODE_TYPE_MAP: Record<string, string> = {
  part: 'part',
  port: 'port',
  interface: 'interface',
  block: 'part', // legacy sandbox alias
};

const EDGE_TYPE_MAP: Record<string, string> = {
  connection: 'connection',
  reference: 'connection',
};

/**
 * Map a synthesised edge to a `ConnectionKind` so `ConnectionEdge` can pick
 * a legend icon. `synthesizeAutoViewLayout` emits `data.interfaceType` as
 * the SysML port-interface name (e.g. `EnergyFlow`, `InformationFlow`); the
 * legacy sandbox emitted `data.kind` directly. Accept either.
 */
function deriveEdgeKind(
  sourceType: string,
  data: Record<string, unknown>,
): ConnectionKind | undefined {
  if (sourceType === 'reference') return 'reference';
  const explicit = data.kind;
  if (typeof explicit === 'string') {
    if (
      explicit === 'energy' ||
      explicit === 'material' ||
      explicit === 'mechanical' ||
      explicit === 'information' ||
      explicit === 'reference'
    ) {
      return explicit;
    }
  }
  const ifaceType = data.interfaceType;
  if (typeof ifaceType === 'string') {
    const lower = ifaceType.toLowerCase();
    // Order matters: 'mechanical' must come before 'material' since a future
    // `MechanicalMaterialFlow` would otherwise match material first. Likewise
    // check 'energy' before 'reference' so an `EnergyReference` (synthetic)
    // would still classify as energy.
    if (lower.includes('mechanical')) return 'mechanical';
    if (lower.includes('energy') || lower.includes('electrical')) return 'energy';
    if (lower.includes('information') || lower.includes('signal') || lower.includes('data'))
      return 'information';
    if (lower.includes('material') || lower.includes('fluid')) return 'material';
    if (lower.includes('reference')) return 'reference';
  }
  return undefined;
}

/**
 * Build React Flow node + edge arrays from a persisted layout JSON. Position
 * is taken directly from `node.position`; elkjs is NOT re-invoked. Hierarchy
 * nesting is preserved via `parentNode` + `extent: 'parent'` (Arch §9.3).
 */
export function buildReactFlowGraph(rawLayout: ReactFlowLayoutJson): {
  nodes: Node[];
  edges: Edge[];
} {
  // Apply the content/visibility scope (hide/collapse) BEFORE the container-
  // detection + node/edge build below, so a collapsed container whose children
  // were all hidden renders as a leaf black box and hidden nodes never reach
  // React Flow. A no-op / absent `viewScope` returns the layout unchanged, so
  // this is byte-identical for every legacy / unscoped view.
  const layout = applyViewScope(rawLayout);
  const positions = layout.positions ?? {};
  // Emphasis overlay (DLA) — project the layoutIntent's emphasise / de-emphasise
  // id-sets into a per-node `data.emphasisKind` marker (mirrors `changeKind`),
  // read by PartNode/ConnectionEdge for an accent ring vs. a dim. Empty sets ⇒
  // no marker (byte-identical). Projected AFTER applyViewScope, so a hidden node
  // simply never gets a marker.
  const emphasise = new Set(layout.layoutIntent?.emphasis ?? []);
  const deemphasise = new Set(layout.layoutIntent?.deemphasis ?? []);
  // First pass: collect parentNode references so we can mark which nodes
  // are containers (i.e., are referenced as a parent by at least one other
  // node). PartNode renders ContainerBody vs LeafBody off this flag, so
  // without it nested IBDs render flat.
  const containerIds = new Set<string>();
  for (const raw of layout.nodes) {
    const parentId = (raw as { parentNode?: string }).parentNode;
    if (typeof parentId === 'string' && parentId.length > 0) {
      containerIds.add(parentId);
    }
  }
  const nodes: Node[] = layout.nodes.map((raw) => {
    const sourceType = typeof raw.type === 'string' ? raw.type : 'part';
    const mappedType = NODE_TYPE_MAP[sourceType] ?? 'part';
    const overridePos = positions[raw.id];
    const position = overridePos ?? raw.position ?? { x: 0, y: 0 };
    const dataIn = (raw.data ?? {}) as Record<string, unknown>;
    const isContainer = containerIds.has(raw.id);
    const emphasisKind = emphasise.has(raw.id)
      ? 'emphasize'
      : deemphasise.has(raw.id)
        ? 'deemphasize'
        : undefined;
    const data: Record<string, unknown> =
      isContainer || emphasisKind !== undefined ? { ...dataIn } : dataIn;
    if (isContainer) data.isContainer = true;
    if (emphasisKind !== undefined) data.emphasisKind = emphasisKind;
    const parentId = (raw as { parentNode?: string }).parentNode;
    const node: Node & { parentId?: string; extent?: 'parent' } = {
      id: raw.id,
      type: mappedType,
      position,
      data,
    };
    if (parentId !== undefined) {
      node.parentId = parentId;
      node.extent = 'parent';
    }
    return node;
  });

  const edges: Edge[] = layout.edges.map((raw) => {
    const sourceType = typeof raw.type === 'string' ? raw.type : 'connection';
    const mappedType = EDGE_TYPE_MAP[sourceType] ?? 'connection';
    const data = (raw.data ?? {}) as Record<string, unknown>;
    const handles = raw as { sourceHandle?: string; targetHandle?: string };
    const kind = deriveEdgeKind(sourceType, data);
    // Persisted waypoints live on the layout edge itself; the runtime carries
    // them inside `data` so ConnectionEdge can read them in a single place.
    const persistedWaypoints = (raw as { waypoints?: ReadonlyArray<{ x: number; y: number }> })
      .waypoints;
    const decoratedData: Record<string, unknown> = { ...data };
    if (kind !== undefined) decoratedData.kind = kind;
    // The flow NAME is rendered on the connector via `data.label`. The
    // synthesizer already emits it as `label`, but fall back to a `name` key
    // so any edge source that carries the flow name under `name` still shows
    // it (and a connector is never left blank when a name exists).
    if (decoratedData.label === undefined && typeof data.name === 'string' && data.name.length > 0) {
      decoratedData.label = data.name;
    }
    if (Array.isArray(persistedWaypoints) && persistedWaypoints.length > 0) {
      decoratedData.waypoints = persistedWaypoints.map((p) => ({ x: p.x, y: p.y }));
    }
    // DLA — an edge touching a de-emphasised (dimmed) node fades with it so a
    // bright connector never leads into a faded block.
    if (deemphasise.has(raw.source) || deemphasise.has(raw.target)) {
      decoratedData.emphasisKind = 'deemphasize';
    }
    const edge: Edge = {
      id: raw.id,
      source: raw.source,
      target: raw.target,
      type: mappedType,
      data: decoratedData,
    };
    if (handles.sourceHandle !== undefined) edge.sourceHandle = handles.sourceHandle;
    if (handles.targetHandle !== undefined) edge.targetHandle = handles.targetHandle;
    return edge;
  });

  return { nodes, edges: coalesceOverlappingEdges(edges) };
}

/**
 * Coalesce connectors that render as the *identical* line — same source node +
 * source handle AND same target node + target handle. Two distinct KB
 * connections between the exact same port pair (e.g. an `EnergyFlow` and a typed
 * `HVPowerBus` both wiring `BatteryPack.hvOutput → DcDcConverter.hvIn`) otherwise
 * draw as two strokes stacked pixel-on-pixel with their labels piled on top of
 * each other — the "strongly overlapping / unnecessary edges" the user reports.
 * We keep the FIRST edge of each such group (deterministic: layout order wins),
 * summing the collapsed count into `data.multiplicity` — which `<ConnectionEdge>`
 * surfaces as a small `×N` marker on the survivor's label, so the fold is
 * visible, not silent. This folds ACROSS flow-kind on purpose: the server
 * synthesizer (`auto-view-layout-synthesizer.ts`) already merges same-
 * `interfaceType` duplicates, so the pairs that reach here and still overlap are
 * exactly the cross-type ones (an `EnergyFlow` + a typed `HVPowerBus` on one port
 * pair) that stack pixel-on-pixel — the visual clutter the user reported.
 *
 * Trade-off (deliberate, deterministic-render-only): two genuinely different-KIND
 * flows sharing one port pair fold to a single stroke in the survivor's colour, so
 * a distinct flow kind can drop from the connector palette / `<EdgeLegend>` at that
 * spot. This is acceptable because the two strokes were already indistinguishable
 * (identical path), the `×N` marker flags the multiplicity, and every folded
 * connection is preserved verbatim in `layout.json` and the authoritative
 * `view.sysml` source. The fold is RENDER-ONLY: `buildLayoutSnapshot` re-emits
 * from `state.layout.edges`, so a save never drops a folded sibling.
 *
 * A group is left INTACT (never collapsed) when any member carries a changeset
 * `data.changeKind` — in a `<kb-change-tree>` diff two edges on the same port
 * pair with different change kinds is exactly the "this connection's type
 * changed" story the user must still see, so hiding one would corrupt the diff.
 *
 * Direction-sensitive: `A→B` and `B→A` key differently and never merge. A
 * handle-less edge is never a fold candidate (see the in-body note), so the
 * synthetic KB-less flat fallback and fixtures pass through untouched.
 */
export function coalesceOverlappingEdges(edges: Edge[]): Edge[] {
  // Scope: ONLY connectors pinned to the identical DECLARED port participate:
  // both `sourceHandle` and `targetHandle` must be defined. A handle-less edge
  // (no declared port -- the KB-less flat fallback and synthetic fixtures) is
  // never a candidate and always passes through unchanged; real KB views always
  // carry `<port>.s` / `<port>.t` handles, so this loses nothing in production
  // while keeping the fold precisely targeted at same-port overlaps. Output
  // order is stable: the first occurrence of each group keeps its slot.
  const SEP = String.fromCharCode(0); // NUL: cannot appear in a port/node id
  const slotByKey = new Map<string, number>();
  const slots: Edge[][] = [];
  for (const e of edges) {
    const hasHandles =
      e.sourceHandle !== undefined &&
      e.sourceHandle !== null &&
      e.targetHandle !== undefined &&
      e.targetHandle !== null;
    if (!hasHandles) {
      slots.push([e]); // no declared port => never folded
      continue;
    }
    const key = `${e.source}${SEP}${e.sourceHandle}${SEP}${e.target}${SEP}${e.targetHandle}`;
    const idx = slotByKey.get(key);
    if (idx === undefined) {
      slotByKey.set(key, slots.length);
      slots.push([e]);
    } else {
      slots[idx]!.push(e);
    }
  }
  const out: Edge[] = [];
  for (const group of slots) {
    if (group.length === 1) {
      out.push(group[0]!);
      continue;
    }
    // Preserve every edge of a group that participates in a changeset diff.
    const anyChanged = group.some((e) => {
      const ck = (e.data as { changeKind?: unknown } | undefined)?.changeKind;
      return ck === 'added' || ck === 'updated' || ck === 'removed';
    });
    if (anyChanged) {
      out.push(...group);
      continue;
    }
    // Total connections at this port pair = SUM of each member's own
    // multiplicity (default 1). The server synthesizer already merges same-
    // `interfaceType` duplicates and stamps `data.multiplicity`; summing here
    // (rather than using `group.length`) means a server-pre-folded edge is not
    // undercounted when we fold it together with a cross-type sibling.
    const total = group.reduce((sum, e) => {
      const m = (e.data as { multiplicity?: unknown } | undefined)?.multiplicity;
      return sum + (typeof m === 'number' && m >= 1 ? m : 1);
    }, 0);
    const survivor = group[0]!;
    const baseData = (survivor.data ?? {}) as Record<string, unknown>;
    out.push({ ...survivor, data: { ...baseData, multiplicity: total } });
  }
  return out;
}

/**
 * Helper to detect "needs first-render layout" — every node either lacks a
 * position or sits at the origin. The pre-lift sandbox always synthesised
 * positions via elkjs on mount.
 */
export function needsFirstRenderLayout(nodes: ReadonlyArray<Node>): boolean {
  if (nodes.length === 0) return false;
  return nodes.every((n) => {
    const pos = n.position;
    return !pos || (pos.x === 0 && pos.y === 0);
  });
}
